import React, { useState } from "react";
import Modal from "react-modal";
import { Button } from "../../Button";
import { TextField } from "../../TextField";
import { PartnerAccountsService } from "../../../api/generated/services/PartnerAccountsService";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";

interface InvitePartnerAccountModalProps {
  isOpen: boolean;
  companyId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const InvitePartnerAccountModal: React.FC<InvitePartnerAccountModalProps> = ({
  isOpen,
  companyId,
  onClose,
  onSuccess,
}) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!loading) {
      setEmail("");
      setError(null);
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError("Email is required");
      return;
    }

    setLoading(true);

    try {
      await PartnerAccountsService.postApiPartnerAccountsCreatePartnerAccount({
        email,
        contentfulId: companyId,
      });
      handleClose();
      onSuccess();
    } catch (err: any) {
      console.error("Error creating partner account:", err);
      setError(
        err?.body?.message ||
          err?.message ||
          "Failed to send invitation. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      className="modal-content md:max-w-[500px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Invite Partner Account"
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">
          Invite Dashboard User
        </h2>
        <Button onClick={handleClose} variant="icon" disabled={loading}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <p className="text-gray-600 text-sm mb-6">
        Enter the email address of the person you want to invite. They will
        receive an invitation email with a link to set their password and access
        the partner dashboard for this company.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <TextField
          type="email"
          id="invite-email"
          label="Email Address"
          name="email"
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value)
          }
          required
          disabled={loading}
        />

        {error && (
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="submit" isLoading={loading}>
            Send Invitation
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default InvitePartnerAccountModal;
